/**
 * Low-profile user menu for the global top bar (left-aligned, next to the
 * workspace switcher on the right). Shows just the user's name + a small
 * chevron; the dropdown reveals the email, Profile (incl. the Stand-in control centre — STAND-IN.md §9) and Logout.
 *
 * Mirrors WorkspaceSwitcher's ``topbar`` variant styling so the two top-bar
 * controls read as a set. Uses PortalMenu (never clipped by overflow ancestors).
 */
import React, { useRef, useState } from 'react';
import { CircleUserRound, LogOut, UserCog } from 'lucide-react';
import PortalMenu from '@stepflow/ui-shared/components/PortalMenu.jsx';
import ProfileDialog from './ProfileDialog.jsx';

export default function UserMenu({ user, onLogout, compact = false, menuAlign = 'bottom-left', rail = false }) {
  const btnRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [profile, setProfile] = useState(false);   // STAND-IN.md §9 — Profile · Stand-in
  if (!user) return null;

  const name = user.name || user.username || 'User';
  const email = user.email || user.username || '';

  return (
    <div className="relative">
      <button
        type="button"
        ref={btnRef}
        onClick={() => setOpen((v) => !v)}
        // ``rail``: sized + styled like its rail siblings (Home, Apps, Settings — h-10 w-10, a 20px stroke icon)
        className={rail ? `flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400/40 ${open ? 'bg-white text-blue-600 shadow-sm ring-1 ring-gray-200/80' : 'text-gray-500 hover:bg-white/80 hover:text-gray-800'}` : compact
          ? 'inline-flex h-6 w-6 items-center justify-center rounded-full bg-slate-700 text-[10px] font-semibold uppercase text-white hover:bg-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-400/40'
          : 'flex max-w-[220px] items-center gap-1 rounded px-1.5 py-0.5 text-left text-[11px] text-slate-600 transition-colors hover:bg-slate-200/60 hover:text-slate-800 focus:outline-none focus:ring-1 focus:ring-blue-400/40'}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={compact || rail ? `Account — ${name}` : undefined}
        title={compact || rail ? `Account — ${name}` : 'Account'}
        data-testid={rail ? 'rail-account-button' : undefined}
      >
        {rail ? <CircleUserRound className="h-5 w-5" strokeWidth={1.75} aria-hidden /> : null}
        {compact && !rail ? <span aria-hidden>{(name || '?').trim().charAt(0)}</span> : null}
        {compact || rail ? null : <span className="truncate font-medium text-slate-700">{name}</span>}
        {compact || rail ? null : <svg
          className={`h-3 w-3 shrink-0 text-slate-400 transition ${open ? 'rotate-180' : ''}`}
          fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>}
      </button>

      <PortalMenu anchorRef={btnRef} open={open} onClose={() => setOpen(false)}
        align={menuAlign} offset={8} width={240}>
        <div className="px-3 py-2.5 border-b border-gray-100">
          <div className="truncate text-sm font-medium text-gray-800">{name}</div>
          {email ? <div className="mt-0.5 truncate text-xs text-gray-500">{email}</div> : null}
        </div>
        <button
          type="button"
          onClick={() => { setOpen(false); setProfile(true); }}
          data-testid="user-menu-profile"
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
        >
          <UserCog className="h-4 w-4" aria-hidden /> Profile
        </button>
        <button
          type="button"
          onClick={() => { setOpen(false); onLogout?.(); }}
          data-testid="user-menu-logout"
          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-red-600 hover:bg-red-50"
        >
          <LogOut className="h-4 w-4" aria-hidden /> Logout
        </button>
      </PortalMenu>
      {profile ? <ProfileDialog user={user} onClose={() => setProfile(false)} /> : null}
    </div>
  );
}
